import {
  LandingSection,
  LandingSectionContent,
} from "@/components/landing-page/landing-page-section";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Link } from "@/components/ui/link";
import { Separator } from "@/components/ui/separator";
import { instructors } from "@/content/instructors.json";
import MoveLeftIcon from "lucide-solid/icons/move-left";
import { type ComponentProps, For, createSignal, splitProps } from "solid-js";

export type Instructor = (typeof instructors)[number];

export default function AboutPage() {
  return (
    <main class="relative min-h-full w-full">
      <LandingSection class="grow">
        <div class="bg-geometric">
          <LandingSectionContent class="text-balance md:min-h-[calc(3/5*100svh)]">
            <Card class="order-last flex grow flex-col p-4 pb-4 md:order-first md:p-12 md:pb-4 lg:p-20 lg:pb-4">
              <CardHeader class="mb-8 md:mb-12 lg:mb-12">
                <h1 class="mb-2.5 font-semibold text-3xl leading-tight tracking-normal md:text-4xl lg:mb-12 lg:text-6xl lg:leading-[1.1]">
                  Meet your workshop instructors
                </h1>
                <h2 class="mb-3.5 font-semibold text-muted-foreground text-xl tracking-tight md:text-2xl lg:text-3xl dark:text-orange-50">
                  Our team brings decades of combined experience in team
                  coaching, leadership development, and strategic planning.
                </h2>
                <h3 class="text-lg text-muted-foreground md:text-xl dark:text-orange-50">
                  Together, we'll help you help your teams to reach their full
                  potential.
                </h3>
              </CardHeader>

              <CardContent class="space-y-4 pt-6 md:space-y-8 lg:space-y-12">
                <For each={instructors}>
                  {(member) => <TeamMemberCard instructor={member} />}
                </For>
              </CardContent>
              <CardFooter class="mt-6">
                <Link href="/">
                  <MoveLeftIcon class="mr-2 size-4" />
                  Back to home
                </Link>
              </CardFooter>
            </Card>
          </LandingSectionContent>
        </div>
      </LandingSection>
    </main>
  );
}

function TeamMemberCard(
  props: ComponentProps<"div"> & { instructor: Instructor },
) {
  const [local, rest] = splitProps(props, ["class", "instructor"]);
  const [showMore, setShowMore] = createSignal(false);

  return (
    <Card bool:data-expanded={showMore()} class="group shrink grow-0" {...rest}>
      <div class="flex flex-col rounded-lg bg-background p-0 md:flex-row">
        <div class="aspect-square w-full md:w-1/2">
          <img
            src={local.instructor.image}
            alt={local.instructor.name}
            class="h-full w-full rounded-t-lg bg-muted/5 object-contain md:rounded-t-none md:rounded-l-lg"
          />
        </div>
        <CardContent class="flex w-full flex-col text-balance p-0 md:aspect-square md:w-1/2">
          <div class="mx-4 my-4 md:my-4 md:mt-4 lg:mx-8">
            <p class="pointer-events-none select-none font-medium font-mono text-muted-foreground/50 text-sm uppercase">
              Instructor
            </p>
            <h4 class="mt-1 font-semibold text-3xl text-foreground lg:text-4xl">
              {local.instructor.name}
            </h4>
          </div>
          <Separator class="border-border" />
          <div class="relative mt-4 flex-1 overflow-y-auto px-4 md:px-0 md:pl-4 lg:px-8">
            <p class="line-clamp-5 pb-4 indent-4 text-base text-muted-foreground group-data-expanded:line-clamp-none md:line-clamp-none md:text-lg">
              {local.instructor.bio}
              <span class="absolute inset-0 flex flex-col justify-end gap-2 rounded-b-lg bg-linear-to-b from-transparent via-transparent to-black align-top group-data-expanded:hidden md:hidden">
                <button
                  type="button"
                  class="font-bold text-accent-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowMore((prev) => !prev);
                  }}
                >
                  Show more
                </button>
              </span>
            </p>
          </div>
        </CardContent>
      </div>
    </Card>
  );
}
